import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Location } from 'react-router-dom';
import { Link, useLocation } from 'react-router-dom';

import { mapListRoutes } from '@/app/config/routes';
import { useCurrentRoute } from '@/app/lib/navigation/use-current-route';
import { useIsAuth } from '@/entities/user/session';

import { HOME } from '@/shared/config/routes';
import { RouteItem } from '@/app/model/route';
import { filterTree } from '@/shared/lib/utils/tree/filter-tree';
import { Breadcrumbs, Menu, Text } from '@mantine/core';
import { type TFunction } from 'i18next';

const generatePathParts = (pathStr: string) => {
  const pathWithoutQuery = pathStr.split('?')[0];
  return pathWithoutQuery.split('/').filter((v) => v.length > 0);
};

// Recursive menu generator using Mantine's new Submenu API
function generateMenu(route: RouteItem, t: TFunction) {
  const children = filterTree(route.children, 'children', (x) => !x.hidden);

  return children.map((x) => {
    if (x.children && x.children.length > 0) {
      return (
        <Menu.Sub key={x.to} closeDelay={100}>
          <Menu.Sub.Target>
            <Menu.Sub.Item>{t(x.label, { ns: 'navigation' })}</Menu.Sub.Item>
          </Menu.Sub.Target>
          <Menu.Sub.Dropdown>{generateMenu(x, t)}</Menu.Sub.Dropdown>
        </Menu.Sub>
      );
    } else {
      return (
        <Menu.Item component={Link} to={x.to} key={x.to}>
          {t(x.label, { ns: 'navigation' })}
        </Menu.Item>
      );
    }
  });
}

function generateBreadcrumbs(t: TFunction, location: Location) {
  const asPathNestedRoutes = generatePathParts(location.pathname.substring(1));

  const crumblist = asPathNestedRoutes.map((subpath, idx) => {
    const href = '/' + asPathNestedRoutes.slice(0, idx + 1).join('/');
    const route = mapListRoutes.get(href);

    if (!route.children || route.children.length === 0) {
      const dataTestID = `breadcrumb${route.to.replaceAll('/', '-')}`;

      return (
        <Text
          component={Link}
          key={route.to}
          to={route.to}
          c={'dimmed'}
          size={'sm'}
          className={'animate-fade'}
          data-testid={dataTestID}
        >
          {t(route.label, { ns: 'navigation' })}
        </Text>
      );
    }

    // If route has children, render as a menu with submenu
    return (
      <Menu
        key={route.label}
        position='bottom-start'
        width={220}
        trigger='click-hover'
        closeDelay={100}
      >
        <Menu.Target>
          <Text c={'dimmed'} className={'animate-fade'} size={'sm'}>
            {t(route.label, { ns: 'navigation' })}
          </Text>
        </Menu.Target>
        <Menu.Dropdown>{generateMenu(route, t)}</Menu.Dropdown>
      </Menu>
    );
  });

  return [
    <RouteLink key={HOME} to={HOME} label={t('main', { ns: 'navigation' })} />,
    ...crumblist,
  ];
}

export default function DynamicBreadcrumbs() {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const currentRoute = useCurrentRoute();
  const isAuth = useIsAuth();
  const hidden = !isAuth || currentRoute?.show.breadcrumbs === false;

  const breadcrumbs = useMemo(() => {
    if (hidden || !currentRoute) {
      return [];
    }
    let res = generateBreadcrumbs(t, location);
    if (!Array.isArray(res)) {
      return [];
    }
    return res;
  }, [location, hidden, currentRoute, i18n.language]);

  if (hidden || !currentRoute) {
    return null;
  }

  return (
    <Breadcrumbs separator={<Text c={'dimmed'}>/</Text>}>
      {breadcrumbs}
    </Breadcrumbs>
  );
}

function RouteLink({ label, to }) {
  const dataTestID = `breadcrumb${to.replaceAll('/', '-')}`;
  return (
    <Text
      component={Link}
      to={to}
      c={'dimmed'}
      data-testid={dataTestID}
      size={'sm'}
      className={'animate-fade'}
    >
      {label}
    </Text>
  );
}